/**
 * RouteRenderManager draws several candidate routes on the map at once with distinct colours, while
 * keeping the existing single global `window.directionsRenderer` as the renderer for the *active*
 * route. This matters for backward compatibility: BaseLayer.hideOutOfRangeMarkers() (the "limit
 * markers to a radius around the route" feature), the CSV export and bookmarking all read
 * `window.directionsRenderer.getDirections()` / `.routeIndex`. By always pointing the primary renderer
 * at whichever route the user has selected, those features keep working untouched.
 *
 * Non-active routes are drawn with additional, dimmed DirectionsRenderer instances that have no panel
 * and suppressed markers, so only the active route shows its turn list (in #directionsList) and A/B
 * pins.
 *
 * On top of the rendered lines, one transparent, wide "hit" Polyline is drawn per route: it carries the
 * hover tooltip (colour + the same metrics shown in the comparison box) and a click-to-select handler.
 * DirectionsRenderer polylines aren't directly hoverable, so this overlay is what makes every route on
 * the map interactive without disturbing the existing rendering.
 */

/* eslint-disable no-undef */

import { tooltipHtml } from "./RouteFormat";
import { HoverTooltip } from "../HoverTooltip";

const ACTIVE_OPACITY = 0.95;
const INACTIVE_OPACITY = 0.45;
const STANDARD_COLOR = "#00ffee";

export class RouteRenderManager {
  /**
   * @param {google.maps.Map} map
   * @param {google.maps.DirectionsRenderer} primaryRenderer The existing window.directionsRenderer.
   */
  constructor(map, primaryRenderer) {
    this.map = map;
    this.primary = primaryRenderer;
    this.secondaries = [];
    this.hoverPolylines = [];
    // A single shared tooltip that leaves pointer events with the route underneath.
    this.tooltip = new HoverTooltip();
    /** @type {Array<{response: object, routeIndex: number, color: string}>} */
    this.options = [];
    this.activeIndex = 0;
  }

  /**
   * Draws all route options and marks `activeIndex` as the selected one.
   *
   * @param {Array<{response: object, routeIndex: number, color: string, label?: string}>} options
   * @param {number} activeIndex
   */
  render(options, activeIndex = 0) {
    this.options = options || [];
    this.activeIndex = activeIndex;
    this.clearSecondaries();

    if (!this.options.length) {
      return;
    }

    let viewportPreserved = false;
    this.options.forEach((option, index) => {
      if (index === activeIndex) {
        this._renderActive(option);
        viewportPreserved = true;
      } else {
        this._renderSecondary(option, viewportPreserved);
      }
    });

    this._drawHoverOverlay();

    // Let the "limit markers to a radius around the route" filter follow the active route.
    this._notifyRadiusFilter();
  }

  /**
   * Makes a different option the active one (re-renders).
   *
   * @param {number} index
   */
  setActive(index) {
    if (index < 0 || index >= this.options.length) {
      return;
    }
    this.render(this.options, index);
  }

  /**
   * @returns {{response: object, routeIndex: number}|null}
   */
  getActiveOption() {
    return this.options[this.activeIndex] || null;
  }

  _renderActive(option) {
    // The primary renderer keeps its panel (#directionsList) and draggable behaviour; we only override
    // its colour so the selected route stands out. polylineOptions only take effect when directions are
    // (re)set, so set options before setDirections.
    this.primary.setOptions({
      polylineOptions: { strokeColor: option.color, strokeOpacity: ACTIVE_OPACITY, strokeWeight: 6, zIndex: 100 },
    });
    this.primary.setMap(this.map);
    this.primary.setDirections(option.response);
    this.primary.setRouteIndex(option.routeIndex || 0);
  }

  _renderSecondary(option, preserveViewport) {
    const renderer = new google.maps.DirectionsRenderer({
      map: this.map,
      suppressMarkers: true,
      preserveViewport: preserveViewport,
      draggable: false,
      polylineOptions: {
        strokeColor: option.color,
        strokeOpacity: INACTIVE_OPACITY,
        strokeWeight: 5,
        zIndex: 10,
      },
    });
    renderer.setDirections(option.response);
    renderer.setRouteIndex(option.routeIndex || 0);
    this.secondaries.push(renderer);
  }

  /**
   * Draws one transparent, wide, clickable Polyline per route on top of the rendered lines, carrying
   * the hover tooltip and click-to-select. A high zIndex ensures it wins mouse events over the
   * DirectionsRenderer polylines below it.
   */
  _drawHoverOverlay() {
    const baseline = this.options.find((o) => o.isBaseline) || this.options[0];
    this.options.forEach((option, index) => {
      const route = option.response.routes[option.routeIndex || 0];
      if (!route || !route.overview_path) {
        return;
      }
      const poly = new google.maps.Polyline({
        path: route.overview_path,
        map: this.map,
        strokeColor: option.color,
        strokeOpacity: 0.01, // effectively invisible, but present for hit-testing
        strokeWeight: 16,
        zIndex: 1000,
        clickable: true,
      });
      poly.addListener("mouseover", (event) => {
        this.tooltip.setContent(tooltipHtml(option, baseline));
        this.tooltip.setPosition(event.latLng);
        this.tooltip.open(this.map);
      });
      poly.addListener("mousemove", (event) => this.tooltip.setPosition(event.latLng));
      poly.addListener("mouseout", () => this.tooltip.close());
      poly.addListener("click", () => {
        this.tooltip.close();
        this.setActive(index);
      });
      this.hoverPolylines.push(poly);
    });
  }

  _clearHoverOverlay() {
    this.tooltip.close();
    this.hoverPolylines.forEach((poly) => poly.setMap(null));
    this.hoverPolylines = [];
  }

  clearSecondaries() {
    this.secondaries.forEach((renderer) => renderer.setMap(null));
    this.secondaries = [];
    this._clearHoverOverlay();
  }

  /**
   * Removes all safety routes and restores the primary renderer to its original standard appearance.
   * Does not clear the primary's directions (the caller decides whether to keep the standard route).
   */
  clear() {
    this.clearSecondaries();
    this.options = [];
    this.primary.setOptions({
      polylineOptions: { strokeColor: STANDARD_COLOR },
    });
  }

  /**
   * Re-fires the same events the radius radios use, so BaseLayer recomputes which markers fall within
   * range of the now-active route.
   */
  _notifyRadiusFilter() {
    const limit = document.querySelector("input[name=limitRadius]");
    if (limit && limit.checked) {
      window.dispatchEvent(new CustomEvent("limitRadiusHasChecked", {}));
    }
  }
}
